import { useEffect, useState } from "react";
import { ApiError, createItem } from "../../../api";

interface AddItemDialogProps {
  submissionId: string;
  lenderName: string;
  /** Forwarded to the worker so the AI subject for the auto-created Zoho task
   *  can reference the deal by name. */
  dealName?: string;
  /** Called with `zohoLinkWarning: true` when the item saved but the Zoho task
   *  link failed — caller surfaces the toast and ⚠ icon. */
  onCreated: (info: { zohoLinkWarning: boolean }) => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Add a single requested-items checklist row. Label only required;
 * notes are optional context. The worker also auto-creates a linked Zoho
 * CRM task on the parent deal — failures surface via `zohoLinkWarning`.
 */
export function AddItemDialog({
  submissionId,
  lenderName,
  dealName,
  onCreated,
  onCancel,
}: AddItemDialogProps) {
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const canSave = label.trim().length > 0 && !running;

  const handleSave = async () => {
    setError(null);
    setRunning(true);
    try {
      const res = await createItem(submissionId, {
        label: label.trim(),
        notes: notes.trim() || null,
        deal_name: dealName,
      });
      await onCreated({ zohoLinkWarning: res.zoho_link_warning });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "save_failed",
      );
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card submission-item-card">
        <div className="write-confirm-title">
          Add item to checklist — {lenderName}
        </div>

        <label className="submission-form-field">
          <span className="submission-form-label">
            Item <span className="submission-form-required">·required</span>
          </span>
          <input
            type="text"
            className="submission-form-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. 3 months bank statements"
            autoFocus
          />
        </label>

        <label className="submission-form-field">
          <span className="submission-form-label">
            Notes <span className="submission-form-hint">·optional</span>
          </span>
          <textarea
            className="submission-form-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Any caveats / context?"
          />
        </label>

        {error && <div className="write-confirm-error">⚠ {error}</div>}

        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={!canSave}
          >
            {running ? "WRITING…" : "ADD ITEM"}
          </button>
        </div>
      </div>
    </div>
  );
}
